import { apiInternals, send } from './client';

/**
 * `request()` is not itself exported from `client.ts` — only `send()` and
 * `apiInternals` are. `apiInternals.request` is the one non-`send()` path a
 * production module outside `client.ts` may use, and only for the reason
 * `api.auditLog` states there: reading `meta` as well as `data`.
 */
const { request } = apiInternals;

/**
 * The device stock registry (M-04) — sensors, readers, the five-row
 * `(kind, code)` type vocabulary, and the CSV stock intake.
 *
 * A separate module from `client.ts` and `reference.ts`, mirroring both: a
 * different audience (`device.manage`, not `reference.manage`) and a
 * different lifetime.
 *
 * ## `list` is built on `request`, never `send`
 *
 * `GET /devices/sensors` and `GET /devices/readers` are CURSOR-PAGINATED —
 * `{ data, meta: { next_cursor, per_page } }` — exactly the shape
 * `client.ts`'s `api.auditLog` composes explicitly rather than reading
 * through `send<T>()`. `send()` resolves to `request()`'s `.data` ALONE and
 * throws `meta` away; a `list` written as `send<DevicePage<Sensor>>(...)`
 * would compile (the cast inside `request()` is unchecked) but resolve to a
 * bare `Sensor[]` with no `.meta` on it at all — the UI's "next page"
 * control would have nothing to read and would simply never appear, with no
 * error anywhere. See M-01c's own report for this exact defect shipped once
 * already.
 *
 * ## `sensorsApi`/`readersApi` take DIFFERENT status unions, DELIBERATELY
 *
 * `Sensor::STATUSES` ends `expired`; `Reader::STATUSES` ends `retired` in
 * its place — a sensor has a shelf-life expiry a reader does not, and a
 * reader is retired outright rather than expiring. `SensorStatus` and
 * `ReaderStatus` are two distinct unions, never one shared type, because a
 * shared union would let a caller write `readersApi.setStatus(uuid,
 * 'expired')` — a call the API refuses with a 422 naming `status` — and the
 * type checker would have nothing to say about it.
 *
 * ## `sensor_type`/`reader_type` on the wire, `type` on the way back
 *
 * `SensorResource`/`ReaderResource` both publish the device's generation as
 * `type` (with `type_label_en`/`type_label_ar` resolved server-side from
 * `device_models`) — but `StoreSensorRequest`/`StoreReaderRequest` read it
 * back as `sensor_type`/`reader_type`. The same asymmetry `reference.ts`
 * documents for `dispensary_uuid` (read) vs `dispensary_id` (write):
 * `SensorInput`/`ReaderInput` name the field to match what the server
 * actually reads, `Sensor`/`Reader` name it to match what the server
 * actually returns.
 *
 * ## The import is a REAL `multipart/form-data` upload
 *
 * Unlike `reference.ts`'s `centresImportApi`, which carries its CSV as a
 * JSON string (`ImportCentresRequest`'s own docblock: "no other
 * file-upload endpoint and no precedent for one" at the time), `POST
 * /devices/{kind}/import` is validated by `ImportDevicesRequest`, whose own
 * docblock is explicit that `file` is "A REAL `multipart/form-data`
 * UPLOAD — DELIBERATELY UNLIKE" the centres one: a real upload is what lets
 * the API reach `Serial::normalise()` with genuinely untrimmed bytes,
 * because Laravel's global `TrimStrings` middleware trims ordinary request
 * fields but never reaches inside an uploaded file. Sending the CSV as a
 * JSON string here — mirroring `centresImportApi`'s code shape rather than
 * this endpoint's actual contract — would fail `ImportDevicesRequest`'s own
 * `'file' => ['required', 'file', ...]` rule outright, since no uploaded
 * file would ever be present on the request. `preview`/`apply` therefore
 * build a `FormData` and pass it straight through `send`/`request`, which
 * (see `client.ts`) forwards a `FormData` body as-is rather than
 * `JSON.stringify`-ing it.
 */

/** The statuses a SENSOR may hold — `Sensor::STATUSES`, the API's own source. */
export type SensorStatus =
  | 'in_stock'
  | 'assigned'
  | 'dispensed'
  | 'active'
  | 'expired'
  | 'faulty'
  | 'replaced'
  | 'returned';

/**
 * The statuses a READER may hold — `Reader::STATUSES`. `retired` WHERE
 * `SensorStatus` HAS `expired`; see this module's own docblock.
 */
export type ReaderStatus =
  | 'in_stock'
  | 'assigned'
  | 'dispensed'
  | 'active'
  | 'faulty'
  | 'replaced'
  | 'returned'
  | 'retired';

/** One entry of the five-row `(kind, code)` vocabulary `GET /devices/models` hands a dropdown. */
export interface DeviceModel {
  uuid: string;
  kind: 'sensor' | 'reader';
  code: string;
  name_en: string;
  name_ar: string;
}

/**
 * One row of a device's lifecycle trail, exactly as `DeviceEventResource`
 * renders it. `to_status` is nullable: `DeviceEventRecorder`'s "registered"
 * event (the one not derived from a transition) carries no target status
 * distinct from the device's own row.
 */
export interface DeviceEvent {
  uuid: string;
  event: string;
  from_status: string | null;
  to_status: string | null;
  reason: string | null;
  occurred_at: string | null;
}

/**
 * `uuid` + `name` OR `null` — never the integer foreign key, the same
 * convention every reference resource in this codebase follows. `null`
 * when the device carries no `dispensary_id` (still in central stock, or
 * never dispensed).
 */
export interface DeviceDispensaryRef {
  uuid: string;
  name: string;
}

/**
 * A sensor in the stock registry. `events` is present only on the detail
 * response (`GET /devices/sensors/{uuid}`) — `SensorResource`'s own
 * `whenLoaded()` omits the key entirely on the list response rather than
 * serialising it as `null` or `[]`, so it is typed optional here to match
 * the wire, not typed away with a second, near-identical interface.
 */
export interface Sensor {
  uuid: string;
  serial_number: string;
  type: string;
  type_label_en: string | null;
  type_label_ar: string | null;
  lot_number: string | null;
  manufactured_on: string | null;
  expires_on: string | null;
  status: SensorStatus;
  dispensary: DeviceDispensaryRef | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  events?: readonly DeviceEvent[];
}

/** A reader in the stock registry — `Sensor`'s sibling; see this module's own docblock. */
export interface Reader {
  uuid: string;
  serial_number: string;
  type: string;
  type_label_en: string | null;
  type_label_ar: string | null;
  model: string | null;
  firmware_version: string | null;
  warranty_expires_on: string | null;
  status: ReaderStatus;
  dispensary: DeviceDispensaryRef | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  events?: readonly DeviceEvent[];
}

/**
 * The query fields `GET /devices/sensors`/`GET /devices/readers` share,
 * parameterised on the per-kind status union so `SensorQuery`/`ReaderQuery`
 * cannot accept the other kind's status.
 */
export interface DeviceQuery<TStatus extends string> {
  status?: TStatus;
  type?: string;
  dispensary?: string;
  serial?: string;
  cursor?: string;
  per_page?: number;
}

/**
 * `expiring_before` is SENSOR-ONLY — `warranty_expires_on` is a commercial
 * date, not a usability one, and `ReaderIndexRequest` deliberately carries
 * no equivalent filter. See this module's own docblock; do not add one to
 * `ReaderQuery` to "match" this.
 */
export interface SensorQuery extends DeviceQuery<SensorStatus> {
  expiring_before?: string;
}

export type ReaderQuery = DeviceQuery<ReaderStatus>;

/** One page of either registry — cursor-paginated, exactly `AuditPage`'s shape. */
export interface DevicePage<T> {
  data: readonly T[];
  meta: {
    next_cursor: string | null;
    per_page: number;
  };
}

/**
 * `POST /devices/sensors`'s body. `sensor_type`, not `type` — see this
 * module's own docblock. `dispensary_id` is the parent dispensary's UUID,
 * despite the name (the same convention `CentreInput.city_id` follows).
 */
export interface SensorInput {
  serial_number: string;
  sensor_type: string;
  status: SensorStatus;
  lot_number?: string | null;
  manufactured_on?: string | null;
  expires_on?: string | null;
  dispensary_id?: string | null;
  notes?: string | null;
}

/** `POST /devices/readers`'s body — `SensorInput`'s sibling. */
export interface ReaderInput {
  serial_number: string;
  reader_type: string;
  status: ReaderStatus;
  model?: string | null;
  firmware_version?: string | null;
  warranty_expires_on?: string | null;
  dispensary_id?: string | null;
  notes?: string | null;
}

/**
 * `status` is PROHIBITED on `PATCH /devices/{kind}/{uuid}` —
 * `UpdateSensorRequest`/`UpdateReaderRequest` both refuse it outright,
 * since moving a device is `POST .../status`'s job (that is the only way a
 * status change is guaranteed to also write a `device_events` row). Typed
 * out here with `Omit`, not merely left optional, so a caller cannot even
 * compile a PATCH that carries one.
 */
export type SensorUpdateInput = Partial<Omit<SensorInput, 'status'>>;
export type ReaderUpdateInput = Partial<Omit<ReaderInput, 'status'>>;

/**
 * One field an `update` verdict touched — `DeviceImport::classifyRow()`'s
 * own `$diff` entry, published verbatim (`SensorController`/
 * `ReaderController::presentImport()` pass `$row['changes']` straight
 * through with no shape awareness of their own).
 *
 * `before`/`after` are DISPLAY values, already formatted server-side
 * (`DeviceImport::displayValue()` — dates as `Y-m-d`, deliberately never
 * the DB's own storage string on one side and the CSV's typed format on
 * the other). Review round 3, Finding 1: a field-NAME list alone catches
 * only half of M-03's own defect — an unexpected column in play. It says
 * nothing about an EXPECTED column given a destructive value (`status:
 * in_stock -> faulty` and `faulty -> in_stock` are both just `status`),
 * which is where M-03's actual damage (reactivating a deactivated centre)
 * concentrated. `before`/`after` is what closes that half.
 */
export interface DeviceImportChange {
  field: string;
  before: string | number | boolean | null;
  after: string | number | boolean | null;
}

/** One row of a CSV import's per-row verdict, as `presentImport()` publishes it. */
export interface DeviceImportRow {
  row_number: number;
  serial_number: string | null;
  type_code: string | null;
  verdict: 'create' | 'update' | 'unchanged' | 'reject';
  /** Plain, untranslated diagnostic text on a `reject` row. `null` otherwise. */
  reason: string | null;
  /** The fields that changed, each with its before/after value. `null` on
   *  every verdict but `update`. */
  changes: readonly DeviceImportChange[] | null;
}

export interface DeviceImportResult {
  /** A hash of the parsed file content. Opaque — carried back verbatim on apply. */
  preview_token: string;
  /** `false` on the dry run, `true` once this response reflects a real apply. */
  applied: boolean;
  counts: { create: number; update: number; unchanged: number; reject: number };
  rows: readonly DeviceImportRow[];
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL
 * — the identical helper `client.ts`'s own (unexported) `queryString`
 * implements for `AuditQuery`, redeclared here because that one is not
 * exported (mirroring `reference.ts`'s own local `query()`, for the same
 * reason).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const deviceModelsApi = {
  /** Any authenticated, active account — the same open read every device GET allows. */
  list: () => send<readonly DeviceModel[]>('/devices/models'),
};

export const sensorsApi = {
  /**
   * DELIBERATELY NOT `send<DevicePage<Sensor>>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: SensorQuery = {}): Promise<DevicePage<Sensor>> => {
    const { data, meta } = await request<readonly Sensor[]>(`/devices/sensors${queryString(query)}`);
    return { data, meta: meta as DevicePage<Sensor>['meta'] };
  },

  show: (uuid: string) => send<Sensor>(`/devices/sensors/${uuid}`),

  create: (input: SensorInput) => send<Sensor>('/devices/sensors', { method: 'POST', body: input }),

  update: (uuid: string, input: SensorUpdateInput) =>
    send<Sensor>(`/devices/sensors/${uuid}`, { method: 'PATCH', body: input }),

  setStatus: (uuid: string, status: SensorStatus, reason?: string) =>
    send<Sensor>(`/devices/sensors/${uuid}/status`, { method: 'POST', body: { status, reason } }),

  remove: (uuid: string) => send<void>(`/devices/sensors/${uuid}`, { method: 'DELETE' }),
};

export const readersApi = {
  list: async (query: ReaderQuery = {}): Promise<DevicePage<Reader>> => {
    const { data, meta } = await request<readonly Reader[]>(`/devices/readers${queryString(query)}`);
    return { data, meta: meta as DevicePage<Reader>['meta'] };
  },

  show: (uuid: string) => send<Reader>(`/devices/readers/${uuid}`),

  create: (input: ReaderInput) => send<Reader>('/devices/readers', { method: 'POST', body: input }),

  update: (uuid: string, input: ReaderUpdateInput) =>
    send<Reader>(`/devices/readers/${uuid}`, { method: 'PATCH', body: input }),

  setStatus: (uuid: string, status: ReaderStatus, reason?: string) =>
    send<Reader>(`/devices/readers/${uuid}/status`, { method: 'POST', body: { status, reason } }),

  remove: (uuid: string) => send<void>(`/devices/readers/${uuid}`, { method: 'DELETE' }),
};

/**
 * The shared multipart body-building both import objects below use.
 * `previewToken` absent is the dry run; present is the apply — the
 * identical "no token = preview" convention `centresImportApi` uses, here
 * expressed as a real upload rather than a JSON field. `set`, not `append`
 * — a caller re-using a `File` across a preview/apply pair must never
 * accumulate a second `file` entry.
 */
function importDevices(
  kind: 'sensor' | 'reader',
  file: File,
  previewToken?: string,
): Promise<DeviceImportResult> {
  const body = new FormData();
  body.set('file', file);
  if (previewToken !== undefined) body.set('preview_token', previewToken);

  return send<DeviceImportResult>(`/devices/${kind}s/import`, { method: 'POST', body });
}

export const sensorsImportApi = {
  /** No `preview_token` sent — writes nothing (`SensorController::import()`'s own docblock). */
  preview: (file: File) => importDevices('sensor', file),

  /**
   * The SAME file, re-sent, plus the token the dry run returned. Can answer
   * a `409` (the content hash matches but the database moved since the
   * preview — a fresh preview is required) OR a `422` (the token does not
   * match the re-parsed file at all — a genuinely different file). Both
   * throw the SAME `ApiError`, but distinguishable by `status`: a caller
   * that only branched on `code` could not tell them apart, since the API
   * answers both with `validation_failed` today. See `SensorController::
   * import()`'s own docblock for the two refusals this mirrors.
   */
  apply: (file: File, previewToken: string) => importDevices('sensor', file, previewToken),
};

export const readersImportApi = {
  preview: (file: File) => importDevices('reader', file),

  apply: (file: File, previewToken: string) => importDevices('reader', file, previewToken),
};
